import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { isUserLicensed, UserRoles } from '@bb/users';
import { Menu as AntdMenu } from 'antd';
import { AuthContext } from 'contexts';
import { featureEnabled } from 'utils';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import Icon from '../../Icon';
import { StyledMenu } from '../StyledComponents';
import { StyledMenuProps } from './types';

const BrokerageMenu: React.FC<StyledMenuProps> = ({
  selectedKey,
  disableToolTip,
  renderBadgeItem,
  getIconColor,
}) => {
  const { auth, hasFeature } = React.useContext(AuthContext);
  const features = auth?.org.features;

  const shouldDisplayDashboard = useMemo(() => (
      isUserLicensed(auth) ||
      auth.role === UserRoles.Agent ||
      (auth.role === UserRoles['Team Admin'] && hasFeature('TEAMS'))
    ), [auth]);

  // Will return true if the the keyholder FF is off OR
  // if there is no subscription OR
  // if the subscription status is not deactivated and suspended
  const shouldDisplayAccessFeatures = useMemo(
    () =>
      !featureEnabled('keyholderBillingFeature') ||
      !auth?.supraSubscription ||
      (auth.supraSubscription.status !==
        SupraSystemSubscriptionStatus.deactivated &&
        auth.supraSubscription.status !==
          SupraSystemSubscriptionStatus.suspended),
    [auth?.supraSubscription],
  );

  const analyticsLegacyOff =
    featureEnabled('analyticsLegacyTurnOffFeature') &&
    auth?.org?.onboardingPortalId &&
    (process.env.TABLEAU_ANALYTICS_PORTAL_ID?.split(',') || []).includes(
      auth.org.onboardingPortalId.toString(),
    );

  return (
    <StyledMenu
      mode="inline"
      selectedKeys={[selectedKey]}
      $hasSupraRebrandingFeature={featureEnabled('supraRebrandingFeature', 1)}
    >
      {(hasFeature('MY_BROKERAGE') || auth.role === UserRoles['Team Admin']) &&
        renderBadgeItem?.('queue', '/admin/dashboard')}
      {hasFeature('MY_BROKERAGE') && (
        <AntdMenu.Item
          key="1"
          icon={<Icon type="home" color={getIconColor('1')} />}
          title={disableToolTip ? null : 'My Brokerage'}
        >
          <Link to="/listing/list/brokerage/0">
            <span>Brokerage</span>
          </Link>
        </AntdMenu.Item>
      )}
      {shouldDisplayDashboard && (
        <AntdMenu.Item
          key="2"
          icon={
            <Icon type="dashboard" color={getIconColor('2')} offset={[0, 2]} />
          }
          title={disableToolTip ? null : 'My Business'}
        >
          <Link to="/my_business">
            <span>Dashboard</span>
          </Link>
        </AntdMenu.Item>
      )}
      {shouldDisplayAccessFeatures && (
        <AntdMenu.Item
          key="13"
          icon={<Icon type="antdUnlockFilled" color={getIconColor('12')} />}
          title={disableToolTip ? null : 'Lockboxes'}
        >
          <Link to="/lockboxes">
            <span>Lockboxes</span>
          </Link>
        </AntdMenu.Item>
      )}

      {hasFeature('SUPRA_MVP') &&
        auth.role === UserRoles.Agent &&
        shouldDisplayAccessFeatures && (
          <AntdMenu.Item
            key="14"
            icon={<Icon type="activity" color={getIconColor('13')} />}
            title={disableToolTip ? null : 'My Activities'}
          >
            <Link to={`/lockboxesActivity?userIds=${auth.id}`}>
              <span>My Activities</span>
            </Link>
          </AntdMenu.Item>
        )}
      {renderBadgeItem?.('showings', '/my_business/showings/buyer')}
      {auth?.team && hasFeature('TEAMS') && (
        <AntdMenu.Item
          key="4"
          icon={<Icon type="people" color={getIconColor('4')} />}
          title={disableToolTip ? null : 'My Team'}
        >
          <Link to="/listing/list/team/0">
            <span>My Team</span>
          </Link>
        </AntdMenu.Item>
      )}
      <AntdMenu.Item
        key="5"
        icon={
          <Icon type={'favorite'} color={getIconColor('5')} offset={[0, 1]} />
        }
        title={disableToolTip ? null : 'My Favorites'}
      >
        <Link to="/listing/list/favorites/0">
          <span>My Favorites</span>
        </Link>
      </AntdMenu.Item>
      {hasFeature('MY_BROKERAGE') &&
        (!featureEnabled('orgAdminFeature') || features.includes('ADMIN')) &&
        renderBadgeItem?.('tools', '/admin')}
      {hasFeature('NETWORK') && (
        <AntdMenu.Item
          key="7"
          icon={<Icon type="planet" color={getIconColor('7')} />}
          title={disableToolTip ? null : 'Exclusive Network'}
        >
          <Link to="/listing/network_map">
            <span>Exclusive Network</span>
          </Link>
        </AntdMenu.Item>
      )}
      {hasFeature('ANALYTICS') &&
        !featureEnabled('analyticsEnhanceFeature') && (
          <AntdMenu.Item
            key="8"
            icon={<Icon type="chart" color={getIconColor('8')} />}
            title={disableToolTip ? null : 'Analytics'}
          >
            <Link to="/analytics/market">
              <span>Analytics</span>
            </Link>
          </AntdMenu.Item>
        )}

      {hasFeature('SHOWING_INSIGHTS') &&
        featureEnabled('analyticsEnhanceFeature') && (
          <AntdMenu.Item
            key="10"
            icon={<Icon type="antdFileTextFilled" color={getIconColor('10')} />}
            title={disableToolTip ? null : 'Analytics Showing Insights'}
            data-cy="market-insight"
          >
            <Link to="/analytics/insight">
              <span>Showing Insights</span>
            </Link>
          </AntdMenu.Item>
        )}

      {hasFeature('ANALYTICS') && featureEnabled('analyticsEnhanceFeature') && (
        <AntdMenu.SubMenu
          key={'analytics-menu'}
          icon={<Icon type="chart" color={getIconColor(['8', '9'])} />}
          title={disableToolTip ? null : 'Analytics'}
          data-cy="analytics-menu"
        >
          {!analyticsLegacyOff && (
            <AntdMenu.Item key="8" data-cy="marketTrends-menu">
              <Link to="/analytics/market">
                <span>Market Trends</span>
              </Link>
            </AntdMenu.Item>
          )}
          {!analyticsLegacyOff && (
            <AntdMenu.Item key="9" data-cy="myBrokerage-menu">
              <Link to="/analytics/dashboard">
                <span>My Brokerage</span>
              </Link>
            </AntdMenu.Item>
          )}
        </AntdMenu.SubMenu>
      )}

      {(!auth.isAdmin || isUserLicensed(auth)) && (
        <AntdMenu.Item
          key="12-b"
          icon={<Icon type="contacts" color={getIconColor('12')} />}
          title={disableToolTip ? null : 'Contacts'}
        >
          <Link to="/contacts/client">
            <span>Contacts</span>
          </Link>
        </AntdMenu.Item>
      )}
    </StyledMenu>
  );
};

export default BrokerageMenu;
